import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useAuth0 } from '@auth0/auth0-react'
import { ApiError, apiPost, waitForAuth } from '../api/client'
import { creditsReturnOn } from '../lib/creditRenewal'
import { balanceIsMonthly } from '../lib/freeAllowance'
import type { UserProfile } from '../types/user'

const ANON_STORAGE_KEY = 'stt_upload_count'
const DEFAULT_ANON_CREDITS = 2
/** How many times to wait again for the Auth0 token before giving up. The
 * wait itself is bounded, so this is a ceiling on total patience, not a
 * spin. */
const TOKEN_WAIT_ATTEMPTS = 3

export function useCredits() {
  const { isAuthenticated, isLoading: authLoading } = useAuth0()
  const [credits, setCredits] = useState<number>(DEFAULT_ANON_CREDITS)
  const [loading, setLoading] = useState(true)
  const [isSubscribed, setIsSubscribed] = useState(false)
  const [planName, setPlanName] = useState<string | null>(null)
  const [renewsAt, setRenewsAt] = useState<string | null>(null)
  const [bonusDeclined, setBonusDeclined] = useState(false)
  /** The profile could not be read. Not the same as a balance of zero, and
   * it must never be drawn as one — see `hasCredits`. */
  const [profileFailed, setProfileFailed] = useState(false)
  /** One in-flight profile request, shared by every caller. Two of them —
   * the mount effect and the login effect — used to fire together and both
   * call applyProfile, so the number on screen was whichever answered last:
   * `/auth/me` on an account initialize had not reached yet answers with the
   * model default. */
  const inflight = useRef<Promise<void> | null>(null)

  const applyProfile = useCallback((profile: UserProfile) => {
    setCredits(profile.max_uploads_allowed)
    setIsSubscribed(profile.is_subscribed)
    setPlanName(profile.plan_name)
    setRenewsAt(profile.renews_at ?? null)
    setBonusDeclined(!!profile.signup_bonus_declined)
    setProfileFailed(false)
    // This browser has an account, so the anonymous counter has nothing left
    // to promise. It used to be zeroed only by a successful initialize — the
    // one call that fails — so a broken sign-in left "2 free" on screen, and
    // logging out brought it back.
    try {
      localStorage.setItem(ANON_STORAGE_KEY, '0')
    } catch {
      // private mode with storage denied: nothing to sync
    }
  }, [])

  /** The signed-in profile, from ONE call.
   *
   * `POST /auth/initialize` returns the whole UserResponse and does nothing
   * at all once the account is initialized, so it works as the reload path
   * too — and an account that got stuck uninitialized (its first initialize
   * never arrived) is repaired by simply coming back. */
  const fetchProfile = useCallback(async (): Promise<UserProfile> => {
    for (let attempt = 0; ; attempt++) {
      const ready = await waitForAuth()
      if (!ready && attempt < TOKEN_WAIT_ATTEMPTS) continue
      const stored = localStorage.getItem(ANON_STORAGE_KEY)
      const anonCredits = stored !== null ? parseInt(stored, 10) || 0 : DEFAULT_ANON_CREDITS
      try {
        return await apiPost<UserProfile>('/auth/initialize', { anonymous_credits: anonCredits })
      } catch (e) {
        // 401 means the token wasn't there yet, not that anything is wrong
        // with the account — wait for it rather than accept a stranger's
        // answer. Anything else is a real failure and the caller sees it.
        if (e instanceof ApiError && e.status === 401 && attempt < TOKEN_WAIT_ATTEMPTS) continue
        throw e
      }
    }
  }, [])

  const loadCredits = useCallback(async () => {
    if (authLoading) return

    if (isAuthenticated) {
      if (!inflight.current) {
        inflight.current = (async () => {
          try {
            applyProfile(await fetchProfile())
          } catch {
            // Not a zero balance — an unknown one. Drawing it as zero told
            // people their transcriptions had run out and offered them a
            // plan, on the strength of a network hiccup.
            setProfileFailed(true)
          } finally {
            inflight.current = null
          }
        })()
      }
      await inflight.current
    } else {
      const stored = localStorage.getItem(ANON_STORAGE_KEY)
      if (stored !== null) {
        setCredits(Math.max(parseInt(stored, 10) || 0, 0))
      } else {
        localStorage.setItem(ANON_STORAGE_KEY, String(DEFAULT_ANON_CREDITS))
        setCredits(DEFAULT_ANON_CREDITS)
      }
      setProfileFailed(false)
    }
    setLoading(false)
  }, [isAuthenticated, authLoading, applyProfile, fetchProfile])

  useEffect(() => {
    // Deferred a tick: the anonymous branch reads localStorage synchronously,
    // and setState inside the effect's own call stack cascades renders
    const t = setTimeout(() => void loadCredits(), 0)
    return () => clearTimeout(t)
  }, [loadCredits])

  function decrement() {
    if (isAuthenticated) {
      setCredits((prev) => Math.max(prev - 1, 0))
    } else {
      const next = Math.max(credits - 1, 0)
      localStorage.setItem(ANON_STORAGE_KEY, String(next))
      setCredits(next)
    }
  }

  function updateFromResult(remaining: number | null) {
    if (remaining !== null) {
      setCredits(remaining)
    } else {
      decrement()
    }
  }

  /** The server refused an anonymous transcription (per-IP daily cap) — the
   * localStorage counter is lying, sync it to zero. */
  function markAnonExhausted() {
    localStorage.setItem(ANON_STORAGE_KEY, '0')
    setCredits(0)
  }

  /** Kept for the login effect, but it is the same single request now: both
   * entry points share `inflight`, so there is one call and one answer. */
  const initializeOnLogin = loadCredits

  // Nothing is fetched for this: a free account's credits return on the 1st
  // (UTC), the same clock the server stamps the month with
  const renewsOn = useMemo(
    () => creditsReturnOn({ isAuthenticated, isSubscribed, renewsAt }),
    [isAuthenticated, isSubscribed, renewsAt],
  )

  return {
    credits,
    loading,
    decrement,
    updateFromResult,
    markAnonExhausted,
    initializeOnLogin,
    refresh: loadCredits,
    /** Unknown is not empty: when the profile failed to load, let the upload
     * go and let the server answer. It already refuses with a 403 that opens
     * the limit dialog, so nothing is lost — whereas a fabricated zero sends
     * someone to the checkout for transcriptions they still have. */
    hasCredits: credits > 0 || profileFailed,
    /** The profile could not be read — the balance on screen is stale. */
    profileFailed,
    isSubscribed,
    planName,
    /** Whether "left this month" is a true description of `credits`.
     *
     * Being on the free tier is only half of it. The top-up raises a balance
     * to the allowance and never trims one down to it, so a former
     * subscriber sitting on 242 is on the free tier and yet holds nothing
     * monthly — calling those 242 "this month's" announces a reset that
     * never comes (see lib/freeAllowance.ts). */
    monthlyBalance: isAuthenticated && !isSubscribed && balanceIsMonthly(credits),
    /** When this account's credits come back, null when there's nothing
     * honest to say (see lib/creditRenewal.ts). */
    renewsOn,
    /** The zero balance is the IP brake's doing, not the user's spending. */
    bonusDeclined,
  }
}
